import { c as _c } from "react/compiler-runtime";
/**
 * Location-related data fetchers
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createOrUpdateLocation, deleteLocation } from "@/src/actions/location";
import { toast } from "@/src/hooks/use-toast";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASKS_CACHE_KEY, TASK_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Location } from "@/src/models/location";
import { getAllLocations, getLocationById } from "@/src/requests/location";
export const LOCATIONS_CACHE_KEY = "locations";
export const updateLocationsQueryData = (queryClient: QueryClient, updatedLocation: Location) => {
  queryClient.setQueryData<Location[]>([LOCATIONS_CACHE_KEY], (oldData: Location[] = []) => {
    const current = oldData;
    const exists = current.some(loc => loc.id === updatedLocation.id);
    if (exists) {
      return current.map(loc => loc.id === updatedLocation.id ? updatedLocation : loc);
    }
    return [updatedLocation, ...current];
  });

  // Also update the individual location cache if it exists
  if (updatedLocation.id) {
    queryClient.setQueryData([LOCATIONS_CACHE_KEY, updatedLocation.id], updatedLocation);
  }
};
export function useLocations() {
  const query = useQuery({
    queryKey: [LOCATIONS_CACHE_KEY],
    queryFn: async () => {
      try {
        const locations = await getAllLocations();
        return locations || [];
      } catch (error) {
        logger.error("Error in useLocations:");
        throw error;
      }
    },
    retry: false,
    staleTime: Infinity
  });
  return {
    locations: query.data || [],
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch
  };
}
export function useLocationById(locationId, options) {
  const $ = _c(12);
  let t0;
  let t1;
  if ($[0] !== locationId) {
    t0 = [LOCATIONS_CACHE_KEY, locationId];
    t1 = () => getLocationById(locationId);
    $[0] = locationId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = options?.enabled !== false && !!locationId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: Infinity,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: location,
    isFetching,
    isLoading,
    isError
  } = useQuery(t3);
  let t4;
  if ($[7] !== isError || $[8] !== isFetching || $[9] !== isLoading || $[10] !== location) {
    t4 = {
      location,
      isFetching,
      isLoading,
      isError
    };
    $[7] = isError;
    $[8] = isFetching;
    $[9] = isLoading;
    $[10] = location;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
export function useLocationCreateOrUpdateMutation() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  let t2;
  if ($[0] !== queryClient) {
    t0 = async location_0 => {
      await queryClient.cancelQueries({
        queryKey: [LOCATIONS_CACHE_KEY]
      });
      const previousLocations = queryClient.getQueryData([LOCATIONS_CACHE_KEY]) ?? [];
      updateLocationsQueryData(queryClient, location_0);
      return {
        previousLocations
      };
    };
    t1 = savedLocation => {
      updateLocationsQueryData(queryClient, savedLocation);
    };
    t2 = (error, variables, context) => {
      logger.error("Error updating location");
      if (context) {
        queryClient.setQueryData([LOCATIONS_CACHE_KEY], context.previousLocations);
      }
      toast({
        title: "Failed to save location",
        description: "There was an error saving the location. Please try again.",
        variant: "destructive"
      });
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t4 = {
      mutationFn: _temp2,
      onMutate: t0,
      onSuccess: t1,
      onError: t2,
      meta: t3
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useMutation(t4);
}
async function _temp2(location) {
  const result = await createOrUpdateLocation(location, true);
  if (!result) {
    throw new Error("Failed to update location");
  }
  return result;
}
export function useDeleteLocationMutation() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  let t2;
  if ($[0] !== queryClient) {
    t0 = async t3 => {
      const {
        locationId: locationId_0
      } = t3;
      await queryClient.cancelQueries({
        queryKey: [LOCATIONS_CACHE_KEY]
      });
      const previousLocations = queryClient.getQueryData([LOCATIONS_CACHE_KEY]) ?? [];
      queryClient.setQueryData([LOCATIONS_CACHE_KEY], t4 => {
        const locations = t4 === undefined ? [] : t4;
        return locations.filter(loc => loc.id !== locationId_0);
      });
      return {
        previousLocations
      };
    };
    t1 = (error, t5, context) => {
      const {
        locationId: locationId_1
      } = t5;
      if (context) {
        queryClient.setQueryData([LOCATIONS_CACHE_KEY], context.previousLocations);
      }
      logger.error("Error deleting location:", {
        locationId: locationId_1,
        error
      });
      toast({
        title: "Location deletion failed",
        description: "There was an error deleting the location. Please try again.",
        variant: "destructive"
      });
    };
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: [LOCATIONS_CACHE_KEY]
      });
      toast({
        title: "Location deleted successfully"
      });
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t4 = {
      mutationFn: _temp3,
      onMutate: t0,
      onError: t1,
      onSuccess: t2,
      meta: t3
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useMutation(t4);
}
async function _temp3(t0) {
  const {
    locationId
  } = t0;
  return deleteLocation({
    locationId
  });
}